/**
 * EC2 Status Broadcast Service
 * 
 * Subscribes to Server-Sent Events (SSE) to receive real-time EC2 status updates
 * across all projects. This allows all users to see when any project's EC2 instance
 * is being launched, stopped, or becomes ready.
 */

import { baseApi } from '../API/api';

class EC2StatusBroadcastService {
  constructor() {
    this.eventSource = null;
    this.subscribers = new Set();
    this.isConnected = false;
  }

  /**
   * Subscribe to EC2 status broadcasts
   * @param {Function} callback - Callback function (eventType, projectId, data)
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (!callback) {
      console.warn('EC2StatusBroadcastService: Invalid subscribe parameters');
      return () => {};
    }

    this.subscribers.add(callback);

    // Start SSE connection if this is the first subscriber
    if (this.subscribers.size === 1) {
      this.startStreaming();
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(callback);
    };
  }

  /**
   * Unsubscribe from EC2 status broadcasts
   * @param {Function} callback - Callback function to remove
   */
  unsubscribe(callback) {
    this.subscribers.delete(callback);

    // Stop streaming if no more subscribers
    if (this.subscribers.size === 0) {
      this.stopStreaming();
    }
  }

  /**
   * Start SSE streaming
   */
  startStreaming() {
    if (this.eventSource) {
      return; // Already streaming
    }

    // Create EventSource for SSE
    const eventSource = new EventSource(`${baseApi}/api/ec2/status/stream`);
    this.eventSource = eventSource;
    this.isConnected = false;

    eventSource.onopen = () => {
      this.isConnected = true;
      console.log('📡 EC2 status broadcast SSE stream connected');
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.error) {
          console.error('📡 ❌ SSE error in EC2 status broadcast:', data.error);
          // Notify subscribers of error
          this.subscribers.forEach(callback => {
            try {
              callback('error', null, data);
            } catch (err) {
              console.error('Error in error callback:', err);
            }
          });
          return;
        }

        // Notify all subscribers
        const eventType = data.type || 'unknown';
        const projectId = data.project_id;
        
        this.subscribers.forEach(callback => {
          try {
            callback(eventType, projectId, data);
          } catch (error) {
            console.error('Error in broadcast callback:', error);
          }
        });
      } catch (error) {
        console.error('📡 ❌ Error parsing EC2 status broadcast data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('📡 ❌ EC2 status broadcast SSE connection error:', error);
      console.error(`📡 ❌ EventSource readyState: ${eventSource.readyState} (0=CONNECTING, 1=OPEN, 2=CLOSED)`);
      
      // If connection is closed, log more details
      if (eventSource.readyState === EventSource.CLOSED) {
        console.error('📡 ❌ EC2 status broadcast SSE connection closed. Will attempt to reconnect...');
        this.isConnected = false;
      }
      
      // EventSource will auto-reconnect, but notify subscribers
      this.subscribers.forEach(callback => {
        try {
          callback('error', null, { error: 'Connection error' });
        } catch (err) {
          console.error('Error in error callback:', err);
        }
      });
    };

    console.log('📡 📡 EC2 status broadcast SSE stream started');
  }

  /**
   * Stop SSE streaming
   */
  stopStreaming() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
      console.log('📡 📡 EC2 status broadcast SSE stream stopped');
    }
  }
}

// Export singleton instance
const ec2StatusBroadcastService = new EC2StatusBroadcastService();
export default ec2StatusBroadcastService;
